import React from "react";
import { Page, PageTitle } from "components/ui/Page";
import {
  PageColumn,
  EvenThirdsLayout,
  ColumnUnlessBreakpoint,
  ColumnAtBreakpoint,
} from "components/ui/PageColumns";
import { LargeSpacer } from "components/ui/VerticalSpacers";
import { AdvisingResourcesCard } from "components/cards/AdvisingResourcesCard";
import { BCalScheduleCard } from "components/cards/BCalScheduleCard";
import { NewAdmitResourcesCard } from "components/cards/NewAdmitResourcesCard";
import { NotificationsCard } from "components/cards/NotificationsCard";
import { StudentLookupCard } from "components/cards/StudentLookupCard";
import { StudentResourcesCard } from "components/cards/StudentResourcesCard";
import { TasksCard } from "components/cards/TasksCard";
import { WithdrawnAdmissionCard } from "components/cards/WithdrawnAdmissionCard";
import { BConnectedSetupCard } from "components/cards/BConnectedSetupCard";
import { MyClassesCard } from "components/cards/MyClassesCard";
import GroupsCard from "components/cards/GroupsCard";
import AlumniOverlay from "components/AlumniOverlay";
import NotFoundUnless from "../routes/NotFoundUnless";
import { FacultyResourcesCard } from "components/cards/FacultyResourcesCard";
import { useFeatures } from "components/hooks/useFeatures";
import { useRoles } from "components/hooks/useRoles";
import { useSlice } from "components/hooks/useSlice";
import { useHistoricAcademicRoles } from "components/hooks/useAcademicRoles";
import { SIRCard } from "components/cards/SIRCard";
import { AlumniHomepageCard } from "components/cards/AlumniHomepageCard";

// https://wikihub.berkeley.edu/display/SIS/CalCentral+My+Dashboard+Page
export default function DashboardPage() {
  const isWithdrawnAdmit = useRoles((roles) => roles.withdrawnAdmit);
  const showAlumniOverlay = useAlumniOverlay();

  if (showAlumniOverlay) {
    return <AlumniOverlay />;
  }

  if (isWithdrawnAdmit) {
    return (
      <NotFoundUnless tabName="Dashboard">
        <Page>
          <PageTitle title="My Dashboard" />
          <LargeSpacer />
          <WithdrawnAdmissionCard />
        </Page>
      </NotFoundUnless>
    );
  }

  return (
    <NotFoundUnless tabName="Dashboard">
      <Page>
        <PageTitle title="My Dashboard" />
        <LargeSpacer />
        <EvenThirdsLayout>
          <PageColumn>
            <SIRCard />
            <NewAdmitResourcesCard />
            <BCalScheduleCard />
            <MyClassesCard />
            <GroupsCard />
          </PageColumn>

          <ColumnAtBreakpoint breakpointName="medium">
            <ColumnUnlessBreakpoint breakpointName="medium">
              <AlumniHomepageCard />
              <BConnectedSetupCard />
              <TasksCard />
            </ColumnUnlessBreakpoint>
            <ColumnUnlessBreakpoint breakpointName="medium">
              <NotificationsCard />
              <StudentLookupCard />
              <AdvisingResourcesCard />
              <StudentResourcesCard />
              <FacultyResourcesCard />
            </ColumnUnlessBreakpoint>
          </ColumnAtBreakpoint>
        </EvenThirdsLayout>
      </Page>
    </NotFoundUnless>
  );
}
DashboardPage.displayName = "DashboardPage";

function useAlumniOverlay() {
  const { alumniHomepage } = useFeatures();
  const historicalLaw = useHistoricAcademicRoles((roles) => roles.law);
  const { alumniFormer, faculty, staff } = useRoles((roles) => roles);
  const { actingAsUid, advisorActingAsUid, delegateActingAsUid } =
    useSlice("myStatus");

  return (
    alumniHomepage &&
    alumniFormer &&
    !historicalLaw &&
    !delegateActingAsUid &&
    !faculty &&
    !staff &&
    !actingAsUid &&
    !advisorActingAsUid
  );
}
